import { type ReactNode, useEffect, useState } from 'react';

import { type JsonLd } from '@atlaskit/json-ld-types';
import { extractSmartLinkProvider, extractSmartLinkTitle } from '@atlaskit/link-extractors';
import { useSmartLinkContext } from '@atlaskit/link-provider';

type Status = 'pending' | 'resolved' | 'errored';

/**
 * First-party providers (Confluence, Jira) return a React node; third-party
 * providers (e.g. Google Drive) return a URL string — render via `<img>`.
 */
export type ProviderIcon = { kind: 'node'; node: ReactNode } | { kind: 'url'; url: string };

export type SmartLinkMetadata = {
	title: string | undefined;
	providerIcon: ProviderIcon | undefined;
	status: Status;
};

const PENDING: SmartLinkMetadata = {
	title: undefined,
	providerIcon: undefined,
	status: 'pending',
};

const toProviderIcon = (
	rawIcon: ReactNode | string | undefined,
	fallbackImage: string | undefined,
): ProviderIcon | undefined => {
	if (typeof rawIcon === 'string' && rawIcon) {
		return { kind: 'url', url: rawIcon };
	}
	if (rawIcon) {
		return { kind: 'node', node: rawIcon };
	}
	if (fallbackImage) {
		return { kind: 'url', url: fallbackImage };
	}
	return undefined;
};

/**
 * Resolves a URL via the SmartLink client (ORS) and extracts title + provider icon.
 *
 * Unlike the internal `useSmartLink` hook this does NOT write into the
 * shared Redux store — it keeps the result in component-local state so
 * it works without needing the full Card registration pipeline.
 *
 * Requires a `<SmartCardProvider>` ancestor.
 */
export const useSmartLinkMetadata = (url: string): SmartLinkMetadata => {
	const context = useSmartLinkContext();
	const [metadata, setMetadata] = useState<SmartLinkMetadata>(PENDING);

	const connections = context?.connections;

	useEffect(() => {
		if (!connections) {
			// No SmartCardProvider in the tree — leave state as PENDING so callers
			// can handle the "not ready" case gracefully (e.g. render a skeleton).
			return;
		}

		let cancelled = false;
		setMetadata(PENDING);

		connections.client
			.fetchData(url)
			.then((response: JsonLd.Response) => {
				if (cancelled) {
					return;
				}
				const provider = extractSmartLinkProvider(response);
				setMetadata({
					title: extractSmartLinkTitle(response),
					providerIcon: toProviderIcon(provider?.icon, provider?.image),
					status: 'resolved',
				});
			})
			.catch(() => {
				if (!cancelled) {
					setMetadata({ title: undefined, providerIcon: undefined, status: 'errored' });
				}
			});

		return () => {
			cancelled = true;
		};
	}, [url, connections]);

	return metadata;
};
